import {
    Component,
    Inject,
    ChangeDetectorRef,
    LOCALE_ID,
    OnInit,
    OnDestroy,
    HostListener,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { formatDate, DatePipe } from "@angular/common";
import { Subscription } from 'rxjs';

import { AuthService } from "src/app/_app-core/services/auth/auth.service";
import { InvoiceService } from "src/app/_services/invoice.service";
import { DataExportService } from "src/app/_services/dataExportService";
import * as moment from "moment-timezone";
import { Config } from "../../../../_store/config";

interface QueryStatus {
    queryId: string;
    status: string;
    lastUpdated: Date;
    isUpdating: boolean;
    errorCount: number;
}

interface ClarificationNotification {
    queryId: string;
    message: string;
    isRead: boolean;
}

@Component({
    selector: "app-queries",
    templateUrl: "./queries.component.html",
    styleUrls: ["./queries.component.scss"],
})
export class QueriesComponent implements OnInit, OnDestroy {
    queries = [];
    allQueries = []; 
    filteredQueries = []; 
    fetching = false;
    loading = false;
    isEmpty = false;
    selectedOption = false;
    searchTerm = "";
    displayName: any;
    page = 1;
    pageSize = 10;
    collectionSize: any;
    filterBy: any;
    queryError = false;
    vendorError = false;
    sessionExpired = false;
    errorMsg = "";
    isCondensed: boolean = false;
    currentLayout = {
        isCondensed: true,
    };
    impersonate;
    
    // Status updates
    private statusUpdateSubscription: Subscription;
    private statusCache = new Map<string, QueryStatus>(); // queryId -> QueryStatus
    public isStatusUpdateRunning = false;
    private readonly MAX_RETRY_ATTEMPTS = 3;
    private readonly BATCH_SIZE = 3; // Process 3 queries at a time

    // UI State
    public statusUpdateProgress = {
        total: 0,
        current: 0,
        isVisible: false
    };

    // Notification system for Clarification Required
    public clarificationNotifications: ClarificationNotification[] = [];
    public showNotificationPanel = false;
    public unreadNotificationCount = 0;

    constructor(
        private router: Router,
        private http: HttpClient,
        private authService: AuthService,
        private invoiceService: InvoiceService,
        private exportService: DataExportService,
        private cdref: ChangeDetectorRef,
        private datePipe: DatePipe,
        @Inject(LOCALE_ID) public locale: string
    ) {}

    ngOnInit(): void {
        this.loadQueries();
    }

    ngOnDestroy(): void {
        if (this.statusUpdateSubscription) {
            this.statusUpdateSubscription.unsubscribe();
        }
    }



    async refreshStatuses(): Promise<void> {
        if (this.queries.length === 0 || this.isStatusUpdateRunning) {
            return;
        }

        this.isStatusUpdateRunning = true;
        
        try {
            // Get queries that need status updates
            const queriesToUpdate = this.getQueriesNeedingUpdate();
            
            if (queriesToUpdate.length === 0) {
                return;
            }

            console.log(`Refreshing statuses for ${queriesToUpdate.length} queries...`);
            
            // Show progress indicator
            this.showStatusUpdateProgress(queriesToUpdate.length);
            
            // Process queries in batches
            await this.processQueriesInBatches(queriesToUpdate);
            
        } catch (error) {
            console.error('Error in status refresh:', error);
        } finally {
            this.isStatusUpdateRunning = false;
            this.hideStatusUpdateProgress();
        }
    }

    private getQueriesNeedingUpdate(): any[] {
        // For manual refresh, update all queries on current page
        return this.queries.filter(query => {
            const cachedStatus = this.statusCache.get(query.queryId);
            
            // Update if:
            // 1. Status is "New" (most likely to change)
            // 2. No cache entry exists
            // 3. Cache is older than 5 minutes
            // 4. Error count is less than max retries
            return (
                query.status === 'New' ||
                !cachedStatus ||
                (cachedStatus.lastUpdated < new Date(Date.now() - 5 * 60 * 1000)) ||
                (cachedStatus.errorCount < this.MAX_RETRY_ATTEMPTS)
            );
        });
    }

    private async processQueriesInBatches(queries: any[]): Promise<void> {
        for (let i = 0; i < queries.length; i += this.BATCH_SIZE) {
            const batch = queries.slice(i, i + this.BATCH_SIZE);
            
            // Process batch concurrently
            const batchPromises = batch.map(query => this.updateSingleQueryStatus(query));
            await Promise.allSettled(batchPromises);
            
            // Update progress
            this.statusUpdateProgress.current = Math.min(i + this.BATCH_SIZE, queries.length);
            this.cdref.detectChanges();
            
            // Add delay between batches to avoid overwhelming the API
            if (i + this.BATCH_SIZE < queries.length) {
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }
    }

    private async updateSingleQueryStatus(query: any): Promise<void> {
        const queryId = query.queryId;
        
        // Mark as updating
        this.markQueryAsUpdating(queryId, true);
        
        try {
            const newStatus = await this.fetchStatusFromSapphire(queryId);
            
            if (newStatus && newStatus !== query.status) {
                // Update local query status
                query.status = newStatus;
                
                // Update cache
                this.updateStatusCache(queryId, newStatus);
                
                // Update in database
                await this.updateQueryStatusInDB(queryId, newStatus);
                
                // Check for clarification notifications after status update
                this.checkForClarificationNotifications();
                
            } else {
                // Update cache even if status hasn't changed
                this.updateStatusCache(queryId, query.status);
            }
            
        } catch (error) {
            this.handleStatusUpdateError(queryId, error);
        } finally {
            this.markQueryAsUpdating(queryId, false);
        }
    }

    private async fetchStatusFromSapphire(queryId: string): Promise<string> {
        const headers = new HttpHeaders({
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            // 'int-log-id': '1',
            // 'key': '46166502-173e-4012-bee7-a25196fcd599',
            // 'token': 'cc549ee8-009e-4a4c-aa12-03289e3b97fe'
        });

        const url = `${Config.urls.GETTICKET}?id=${encodeURIComponent(queryId)}`;
        const response: any = await this.http.get(url).toPromise();
        
        return response?.data?.ticket?.currentState?.stateName || null;
    }

    private async updateQueryStatusInDB(queryId: string, newStatus: string): Promise<void> {
        try {
            // Find the query object to get all required fields
            const query = this.queries.find(q => q.queryId === queryId);
            if (!query) {
                console.error(`Query ${queryId} not found in local queries array`);
                return;
            }

            const updatePayload = {
                action: 'update',
                session: {
                    sessionId: localStorage.getItem("eippSession"),
                    username: localStorage.getItem("vendorCode"),
                    sessionTimeout: localStorage.getItem("sessionTimeout") || 30,
                    expiryDate: localStorage.getItem("expiryDate") || (Date.now() + 30 * 60 * 1000)
                },
                sellerCode: this.authService.getSellerCode(),
                supplierCode: localStorage.getItem("vendorCode"),
                externalRequestId: queryId,
                details: {
                    queryId: query.queryId,
                    eDeskTicketNo: query.eDeskTicketNo,
                    category: query.category,
                    crtDtTm: query.crtDtTm,
                    status: newStatus
                }
            };

            await this.http.post(Config.urls.SAVEQUERY, updatePayload).toPromise();
        } catch (error) {
            console.error(`Error updating status in DB for query ${queryId}:`, error);
        }
    }

    private updateStatusCache(queryId: string, status: string): void {
        this.statusCache.set(queryId, {
            queryId,
            status,
            lastUpdated: new Date(),
            isUpdating: false,
            errorCount: 0
        });
    }

    private handleStatusUpdateError(queryId: string, error: any): void {
        const cachedStatus = this.statusCache.get(queryId) || {
            queryId,
            status: '',
            lastUpdated: new Date(0),
            isUpdating: false,
            errorCount: 0
        };
        
        cachedStatus.errorCount++;
        cachedStatus.lastUpdated = new Date();
        
        this.statusCache.set(queryId, cachedStatus);
        
        // If we've exceeded retry attempts, mark as failed
        if (cachedStatus.errorCount >= this.MAX_RETRY_ATTEMPTS) {
            console.warn(`Query ${queryId} status update failed after ${this.MAX_RETRY_ATTEMPTS} attempts`);
        }
    }

    private markQueryAsUpdating(queryId: string, isUpdating: boolean): void {
        const cachedStatus = this.statusCache.get(queryId);
        if (cachedStatus) {
            cachedStatus.isUpdating = isUpdating;
            this.statusCache.set(queryId, cachedStatus);
        }
    }

    private showStatusUpdateProgress(total: number): void {
        this.statusUpdateProgress = {
            total,
            current: 0,
            isVisible: true
        };
        this.cdref.detectChanges();
    }

    private hideStatusUpdateProgress(): void {
        this.statusUpdateProgress.isVisible = false;
        this.cdref.detectChanges();
    }

    // Public methods for UI
    isQueryUpdating(queryId: string): boolean {
        const cachedStatus = this.statusCache.get(queryId);
        return cachedStatus?.isUpdating || false;
    }

    getQueryErrorCount(queryId: string): number {
        const cachedStatus = this.statusCache.get(queryId);
        return cachedStatus?.errorCount || 0;
    }

    // Notification methods for Clarification Required
    checkForClarificationNotifications(): void {
        const clarificationQueries = this.queries.filter(query => 
            query.status?.toLowerCase() === 'clarification required'
        );

        clarificationQueries.forEach(query => {
            const existingNotification = this.clarificationNotifications.find(
                notification => notification.queryId === query.queryId
            );

            if (!existingNotification) {
                const newNotification: ClarificationNotification = {
                    queryId: query.queryId,
                    message: `Query ${query.queryId} requires clarification. Please check your email for details.`,
                    isRead: false
                };
                this.clarificationNotifications.unshift(newNotification);
                this.unreadNotificationCount++;
            }
        });

        // Remove notifications for queries that no longer have 'Clarification Required' status
        this.clarificationNotifications = this.clarificationNotifications.filter(notification => {
            const query = this.queries.find(q => q.queryId === notification.queryId);
            return query && query.status?.toLowerCase() === 'clarification required';
        });

        this.updateUnreadCount();
    }

    updateUnreadCount(): void {
        this.unreadNotificationCount = this.clarificationNotifications.filter(
            notification => !notification.isRead
        ).length;
    }

    toggleNotificationPanel(): void {
        this.showNotificationPanel = !this.showNotificationPanel;
        if (this.showNotificationPanel) {
            // Mark all notifications as read when panel is opened
            this.clarificationNotifications.forEach(notification => {
                notification.isRead = true;
            });
            this.updateUnreadCount();
        }
    }

    markNotificationAsRead(notification: ClarificationNotification): void {
        notification.isRead = true;
        this.updateUnreadCount();
    }

    clearAllNotifications(): void {
        this.clarificationNotifications = [];
        this.unreadNotificationCount = 0;
    }

    onNotificationClick(notification: ClarificationNotification): void {
        // Mark as read
        this.markNotificationAsRead(notification);
        
        // Navigate to query detail
        this.onQueryIdClick(notification.queryId);
        
        // Close notification panel
        this.showNotificationPanel = false;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        const target = event.target as HTMLElement;
        const notificationContainer = target.closest('.notification-container');
        
        if (!notificationContainer && this.showNotificationPanel) {
            this.showNotificationPanel = false;
        }
    }

    getStatusBadgeClass(status: string): string {
        switch (status?.toLowerCase()) {
            case 'new':
                return 'bg-primary';
            case 'assigned to group':
            case 'assigned to executive':
            case 'clarification required':             
            case 'work in progress':    
            case 'Resolved':
            case 'internal ticket':   
            case 'awaiting for user feedback':
                return 'bg-info';
            case 'closed':
                return 'bg-success';
            case 'invalid ticket':
                return 'bg-danger';
            default:
                return 'bg-secondary';
        }
    }

    // Manual refresh method - works for any page
    manualRefreshStatuses(): void {
        if (!this.isStatusUpdateRunning) {
            this.refreshStatuses();
        }
    }

    toggleSidebar() {
        this.currentLayout.isCondensed = !this.currentLayout.isCondensed;
        document.body.setAttribute(
            "data-sidebar-size",
            this.currentLayout.isCondensed ? "condensed" : "default"
        );
    }

    editQuery(queryID) {}

    submitQuery() {
        this.router.navigateByUrl("submit-query");
    }

    accountLogout() {
        localStorage.clear();
        this.router.navigateByUrl("/");
        const userData = {
            session: { sessionId: localStorage.getItem("eippSession") },
        };
        this.authService.logOut(userData).subscribe(
            (res) => {
                const responseData: any = res;
                if (responseData.status === "ok") {
                    localStorage.clear();
                    this.router.navigateByUrl("/");
                }
            },
            (error) => {
                console.log(error);
                localStorage.clear();
                this.router.navigateByUrl("/");
            }
        );
    }

    onFilter(option: any) {
        this.filterBy = option.target.value;
        console.log(this.filterBy + " option selected");
        if (this.filterBy && this.filterBy !== "") {
            this.selectedOption = true;
        } else {
            this.selectedOption = false;
            this.searchTerm = "";
            this.queries = [...this.allQueries];
        }
    }

    exportToExcel() {
        // const date = new Date();
        // const fileName =
        //     (this.displayName = localStorage.getItem("vendorCode")) +
        //     "_Invoices_Table_" +
        //     formatDate(date, "dd_MM_yyyy_h_mm_a", this.locale);
        // if (this.selectedOption === false) {
        //     this.tableData = this.invoices;
        // } else {
        //     this.tableData = this.invs;
        // }
        // const cols = [
        //     {
        //         name: "Invoice No",
        //         colKey: "invoice_number",
        //     },
        //     {
        //         name: "Barcode No",
        //         colKey: "barcode",
        //     },
        //     {
        //         name: "PO No",
        //         colKey: "poNo",
        //     },
        //     {
        //         name: "Invoice Amount",
        //         colKey: "invoice_amount",
        //     },
        //     {
        //         name: "Currency",
        //         colKey: "po_currency",
        //     },
        //     {
        //         name: "Current Status",
        //         colKey: "status",
        //     },
        //     {
        //         name: "Payment Due Date",
        //         colKey: "payment_due_date",
        //     },
        //     {
        //         name: "GST Reconciled",
        //         colKey: "gst_reconciled",
        //     },
        // ];
        // this.exportService.exportToExcel(this.tableData, cols, fileName);
    }

    onSearch(event: KeyboardEvent) {
        this.searchTerm = (event.target as HTMLInputElement).value;

        if (this.searchTerm !== "") {
            if (this.filterBy === "queryId") {
                this.queries = this.allQueries.filter((data) =>
                    data.queryId && 
                    data.queryId.toString()
                        .toLowerCase()
                        .includes(this.searchTerm.toLowerCase())
                );
            } else if (this.filterBy === "eDeskTicketNo") {
                this.queries = this.allQueries.filter((data) =>
                    data.eDeskTicketNo && 
                    data.eDeskTicketNo.toString()
                        .toLowerCase()
                        .includes(this.searchTerm.toLowerCase())
                );
            } else if (this.filterBy === "category") {
                this.queries = this.allQueries.filter((data) =>
                    data.category && 
                    data.category.toString()
                        .toLowerCase()
                        .includes(this.searchTerm.toLowerCase())
                );
            } else if (this.filterBy === "status") {
                this.queries = this.allQueries.filter((data) =>
                    data.status && 
                    data.status.toString()
                        .toLowerCase()
                        .includes(this.searchTerm.toLowerCase())
                );
            }
        } else {
            this.queries = this.allQueries;
        }
    }

    generalSearch(event: KeyboardEvent) {
        this.searchTerm = (event.target as HTMLInputElement).value;
        if (this.searchTerm !== "") {
            this.queries = this.allQueries.filter(
                (data) =>
                    (data.queryId && data.queryId.toString()
                        .toLowerCase()
                        .includes(this.searchTerm.toLowerCase())) ||
                    (data.eDeskTicketNo && data.eDeskTicketNo.toString()
                        .toLowerCase()
                        .includes(this.searchTerm.toLowerCase())) ||
                    (data.category && data.category.toString()
                        .toLowerCase()
                        .includes(this.searchTerm.toLowerCase())) ||
                    (data.status && data.status.toString()
                        .toLowerCase()
                        .includes(this.searchTerm.toLowerCase()))
            );
        } else {
            this.queries = this.allQueries;
        }
    }


    onQueryIdClick(queryId: string) {
        this.router.navigate(['/query-detail', queryId]);
    }

    loadQueries() {
        const req = {
            "sortConfig": {
                "field": "updtDtTm",
                "order": "desc"
            },
            "filters":[{
                "key": "supplierCode",
                "value": localStorage.getItem("vendorCode")             
            }],
            "pageSize": this.pageSize,
            "pageNo": this.page,
            "isExport": false,
            "linkFilters": [],
            "isAP": true
        };
        
        this.loading = true;
        this.invoiceService.fetchQueries(req).subscribe(
            (res: any) => {
                this.queries = res.data.list;
                this.allQueries = [...res.data.list];
                this.collectionSize = res.data.noOfRecords;
                this.loading = false;
                
                this.queries.forEach(query => {
                    if (!this.statusCache.has(query.queryId)) {
                        this.statusCache.set(query.queryId, {
                            queryId: query.queryId,
                            status: query.status,
                            lastUpdated: new Date(),
                            isUpdating: false,
                            errorCount: 0
                        });
                    }
                });
                
                this.checkForClarificationNotifications();
                
                setTimeout(() => this.refreshStatuses(), 2000);
            },
            (error) => {
                console.error('Error loading queries:', error);
                this.loading = false;
                this.errorMsg = 'Failed to load queries. Please try again.';
            }
        );
    }

    refreshQueries() {
        // Reset search when refreshing
        this.searchTerm = "";
        this.selectedOption = false;
        this.filterBy = null;
        this.loadQueries();
    }

    clearSearch() {
        this.searchTerm = "";
        this.selectedOption = false;
        this.filterBy = null;
        this.queries = [...this.allQueries];
    }
}
